import React, { useEffect } from "react";
import { useForm, Controller } from "react-hook-form";
import { Button } from "@shared/ui/button";
import { Label } from "@shared/ui/label";
import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@shared/ui/select";
import { Mail, MessageSquare, BellRing, MessageCircle } from "lucide-react";
import { getTemplateCategories } from "../../utils";

interface TemplateFilters {
  page: number;
  limit: number;
  search_data?: string;
  channelType?: string;
  category?: string;
  status?: "draft" | "published";
  tags?: string;
}

interface TemplateFilterPanelProps {
  currentFilters: TemplateFilters;
  onApplyFilters: (filters: Partial<TemplateFilters>) => void;
  onResetFilters: () => void;
}

export const TemplateFilterPanel: React.FC<TemplateFilterPanelProps> = ({
  currentFilters,
  onApplyFilters,
  onResetFilters,
}) => {
  const form = useForm({
    defaultValues: {
      channelType: currentFilters.channelType || "ALL_CHANNELS",
      category: currentFilters.category || "ALL_CATEGORIES",
      status: currentFilters.status || "ALL_STATUSES",
    },
  });

  useEffect(() => {
    form.setValue("channelType", currentFilters.channelType || "ALL_CHANNELS");
    form.setValue("category", currentFilters.category || "ALL_CATEGORIES");
    form.setValue("status", currentFilters.status || "ALL_STATUSES");
  }, [currentFilters, form]);

  const handleApplyFilters = (data: any) => {
    const filtersToApply: Partial<TemplateFilters> = {};

    if (data.channelType && data.channelType !== "ALL_CHANNELS") {
      filtersToApply.channelType = data.channelType;
    }

    if (data.category && data.category !== "ALL_CATEGORIES") {
      filtersToApply.category = data.category;
    }

    if (data.status && data.status !== "ALL_STATUSES") {
      filtersToApply.status = data.status;
    }

    onApplyFilters(filtersToApply);
  };

  const handleResetFilters = () => {
    form.reset({
      channelType: "ALL_CHANNELS",
      category: "ALL_CATEGORIES",
      status: "ALL_STATUSES",
    });
    onResetFilters();
  };

  const getChannelIcon = (channel: string) => {
    switch (channel) {
      case "EMAIL":
        return <Mail className="h-4 w-4 mr-2" />;
      case "TEXT":
        return <MessageSquare className="h-4 w-4 mr-2" />;
      case "NOTIFICATION":
        return <BellRing className="h-4 w-4 mr-2" />;
      case "WHATSAPP":
        return <MessageCircle className="h-4 w-4 mr-2" />;
      default:
        return null;
    }
  };

  return (
    <form onSubmit={form.handleSubmit(handleApplyFilters)}>
      <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-3 gap-6">
        <div className="space-y-2">
          <Label>Channel</Label>
          <Controller
            name="channelType"
            control={form.control}
            render={({ field }) => (
              <Select value={field.value} onValueChange={field.onChange}>
                <SelectTrigger>
                  <SelectValue placeholder="Select channel" />
                </SelectTrigger>
                <SelectContent>
                  <SelectGroup>
                    <SelectItem value="ALL_CHANNELS">All Channels</SelectItem>
                    <SelectItem value="EMAIL">
                      <div className="flex items-center">
                        {getChannelIcon("EMAIL")}
                        Email
                      </div>
                    </SelectItem>
                    <SelectItem value="TEXT">
                      <div className="flex items-center">
                        {getChannelIcon("TEXT")}
                        SMS
                      </div>
                    </SelectItem>
                    <SelectItem value="NOTIFICATION">
                      <div className="flex items-center">
                        {getChannelIcon("NOTIFICATION")}
                        In-App
                      </div>
                    </SelectItem>
                    <SelectItem value="WHATSAPP">
                      <div className="flex items-center">
                        {getChannelIcon("WHATSAPP")}
                        WhatsApp
                      </div>
                    </SelectItem>
                  </SelectGroup>
                </SelectContent>
              </Select>
            )}
          />
        </div>

        <div className="space-y-2">
          <Label>Category</Label>
          <Controller
            name="category"
            control={form.control}
            render={({ field }) => (
              <Select value={field.value} onValueChange={field.onChange}>
                <SelectTrigger>
                  <SelectValue placeholder="Select category" />
                </SelectTrigger>
                <SelectContent>
                  <SelectGroup>
                    <SelectItem value="ALL_CATEGORIES">
                      All Categories
                    </SelectItem>
                    {getTemplateCategories().map((category) => (
                      <SelectItem key={category} value={category}>
                        {category}
                      </SelectItem>
                    ))}
                  </SelectGroup>
                </SelectContent>
              </Select>
            )}
          />
        </div>

        <div className="space-y-2">
          <Label>Status</Label>
          <Controller
            name="status"
            control={form.control}
            render={({ field }) => (
              <Select value={field.value} onValueChange={field.onChange}>
                <SelectTrigger>
                  <SelectValue placeholder="Select status" />
                </SelectTrigger>
                <SelectContent>
                  <SelectGroup>
                    <SelectItem value="ALL_STATUSES">All Statuses</SelectItem>
                    <SelectItem value="published">Published</SelectItem>
                    <SelectItem value="draft">Draft</SelectItem>
                  </SelectGroup>
                </SelectContent>
              </Select>
            )}
          />
        </div>

        <div className="col-span-1 md:col-span-3 lg:col-span-3 flex justify-end gap-2">
          <Button type="button" variant="outline" onClick={handleResetFilters}>
            Reset Filters
          </Button>
          <Button type="submit">Apply Filters</Button>
        </div>
      </div>
    </form>
  );
};
